import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import SEO from "../../components/SEO";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import { getAlternative, ALTERNATIVES } from "../../data/alternatives";

export default function AlternativePage() {
  const { slug } = useParams();
  const alt = getAlternative(slug);

  if (!alt) return <Navigate to="/alternatives" replace />;

  const title = `Best ${alt.name} Alternative — Collavue`;
  const description = `Looking for a ${alt.name} alternative? Collavue gives you X (Twitter), LinkedIn, and Reddit lead discovery, AI brand-voice replies, and a human-in-the-loop approval queue. ${alt.summary}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    author: { "@type": "Organization", name: "Collavue" },
    publisher: {
      "@type": "Organization",
      name: "Collavue",
      logo: {
        "@type": "ImageObject",
        url: "https://collavue.com/collavue-logo-256.png",
      },
    },
    mainEntityOfPage: `https://collavue.com/alternatives/${alt.slug}`,
  };

  const others = ALTERNATIVES.filter((a) => a.slug !== alt.slug).slice(0, 6);

  return (
    <div className="bg-[#faf6ee] text-[#0a1736] min-h-screen flex flex-col">
      <SEO
        title={title}
        description={description}
        path={`/alternatives/${alt.slug}`}
        keywords={alt.keywords}
        type="article"
        schema={schema}
      />
      <MarketingNav />

      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{
            backgroundImage:
              "radial-gradient(1200px 400px at 80% -20%, rgba(45,91,255,0.10), transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-16 lg:pt-24">
          <nav className="text-sm text-[#5a6478] mb-6">
            <Link to="/" className="hover:text-[#2d5bff]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/alternatives" className="hover:text-[#2d5bff]">Alternatives</Link>
            <span className="mx-2">/</span>
            <span className="text-[#0a1736]">{alt.name}</span>
          </nav>

          <p className="text-[#2d5bff] font-medium text-sm tracking-wide uppercase">
            Best {alt.name} alternative
          </p>
          <h1 className="font-display text-[#0a1736] text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] mt-3 max-w-4xl">
            Looking for a <span className="italic">{alt.name}</span> alternative?{" "}
            <span className="text-[#2d5bff]">Try Collavue.</span>
          </h1>
          <p className="mt-6 text-lg text-[#5a6478] max-w-3xl leading-relaxed">{alt.summary}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/signup"
              className="px-6 py-3 rounded-md bg-[#0a1736] text-white font-medium hover:bg-[#1c2a4d] inline-flex items-center gap-2"
            >
              Try Collavue free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/case-studies"
              className="px-6 py-3 rounded-md bg-white text-[#0a1736] border border-[#e3dccb] font-medium hover:border-[#0a1736]"
            >
              See it in action
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-[#e3dccb]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2 className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold">
            Why teams pick Collavue over {alt.name}
          </h2>
          <p className="mt-3 text-[#5a6478] max-w-3xl">{alt.tagline}</p>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {alt.why.map((w) => (
              <div key={w} className="rounded-xl border border-[#e3dccb] p-6 bg-[#faf6ee]/40">
                <CheckCircle2 className="w-6 h-6 text-[#2d5bff]" />
                <p className="text-[#0a1736] mt-3 leading-relaxed">{w}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#faf6ee] border-t border-[#e3dccb]">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <h2 className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold">
            {alt.name} vs Collavue, feature-by-feature
          </h2>
          <div className="mt-8 overflow-hidden rounded-xl border border-[#e3dccb] bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#0a1736] text-white">
                <tr>
                  <th className="px-5 py-3 font-medium">Feature</th>
                  <th className="px-5 py-3 font-medium">{alt.name}</th>
                  <th className="px-5 py-3 font-medium">Collavue</th>
                </tr>
              </thead>
              <tbody>
                {alt.feature_diff.map((row, i) => (
                  <tr key={i} className="border-t border-[#e3dccb]">
                    <td className="px-5 py-3 text-[#0a1736] font-medium">{row[0]}</td>
                    <td className="px-5 py-3 text-[#5a6478]">{row[1]}</td>
                    <td className="px-5 py-3 text-[#2d5bff] font-medium">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-[#e3dccb]">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <Sparkles className="w-7 h-7 text-[#2d5bff] mx-auto" />
          <h2 className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold mt-4">
            Ready to switch from {alt.name}?
          </h2>
          <p className="mt-3 text-[#5a6478] max-w-2xl mx-auto">
            Spin up a Collavue workspace, plug in your offer, and we'll surface the first
            qualified leads within 24 hours — drafts ready for your approval.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/signup"
              className="px-6 py-3 rounded-md bg-[#0a1736] text-white font-medium hover:bg-[#1c2a4d]"
            >
              Start free
            </Link>
            <Link
              to="/pricing"
              className="px-6 py-3 rounded-md bg-white text-[#0a1736] border border-[#e3dccb] font-medium hover:border-[#0a1736]"
            >
              See pricing
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#0a1736] text-white/80">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2 className="font-display text-2xl md:text-3xl text-white font-semibold">
            More alternatives to compare
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {others.map((a) => (
              <Link
                key={a.slug}
                to={`/alternatives/${a.slug}`}
                className="rounded-lg border border-white/10 bg-white/5 px-4 py-3 hover:border-[#bef264] hover:text-white transition-colors"
              >
                Alternative to {a.name}
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <Link to="/alternatives" className="text-[#bef264] hover:underline">
              See all alternatives →
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
